import { useState } from 'react';
import { s } from '../constants/styles';
import { fmt } from '../utils/date';
import { getDayMeta, getDayExercises, getExerciseHistory, getTrainingDayIndices } from '../utils/workout';
import LineChart from './LineChart';

export default function HistoryView({ data }) {
  const { sessions, customProgram } = data;
  const [sel, setSel] = useState(null);

  const currentIds = new Set();
  getTrainingDayIndices(data.dayStructure).forEach(idx => {
    getDayExercises(idx, customProgram, data.dayStructure).forEach(ex => currentIds.add(ex.id));
  });

  const archivedMap = {};
  Object.values(sessions).forEach(sess => {
    Object.keys(sess.exercises || {}).forEach(exId => {
      if (!currentIds.has(exId) && sess.exercises[exId]?.sets?.length > 0) {
        if (!archivedMap[exId]) archivedMap[exId] = { id: exId, name: sess.exercises[exId].name || exId };
      }
    });
  });

  function getTrend(exId) {
    const hist = getExerciseHistory(sessions, exId, 2);
    if (hist.length < 2) return null;
    const topSet = h => Math.max(...h.sets.filter(s => !s.warmup).map(s => s.weight), 0);
    const a = topSet(hist[0]);
    const b = topSet(hist[1]);
    if (a > b) return "up";
    if (a < b) return "down";
    return "same";
  }

  // Drill-down view
  if (sel) {
    const hist = getExerciseHistory(sessions, sel.id, 20);
    const strengthPoints = hist.slice().reverse().map(h => {
      const ws = h.sets.filter(s => !s.warmup);
      if (!ws.length) return null;
      const top = ws.reduce((best, st) => st.weight > best.weight ? st : best, ws[0]);
      return { y: top.weight, label: fmt(h.date) };
    }).filter(Boolean);
    const volumePoints = hist.slice().reverse().map(h => {
      const ws = h.sets.filter(s => !s.warmup);
      if (!ws.length) return null;
      return { y: ws.reduce((sum, st) => sum + st.weight * st.reps, 0), label: fmt(h.date) };
    }).filter(Boolean);

    return (
      <div className="fade-in" style={{ padding: 20, paddingBottom: 100 }}>
        <button onClick={() => setSel(null)} style={{ ...s.btnG, padding: "10px 16px", fontSize: 13, marginBottom: 16 }}>‹ Back</button>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 16 }}>{sel.name}</h2>

        {strengthPoints.length >= 2 && (
          <div style={{ ...s.card, padding: "14px 14px 10px", marginBottom: 12 }}>
            <div style={{ ...s.label, marginBottom: 8 }}>TOP SET WEIGHT</div>
            <LineChart points={strengthPoints} color="#4f8fff" unit=" lbs" />
          </div>
        )}
        {volumePoints.length >= 2 && (
          <div style={{ ...s.card, padding: "14px 14px 10px", marginBottom: 12 }}>
            <div style={{ ...s.label, marginBottom: 8 }}>SESSION VOLUME</div>
            <LineChart points={volumePoints} color="#a78bfa" unit=" lbs" />
          </div>
        )}

        {hist.length === 0 && <p style={{ color: "var(--text-3)" }}>No data yet</p>}
        {hist.map((h, i) => {
          const ws = h.sets.filter(s => !s.warmup);
          const vol = ws.reduce((sum, st) => sum + st.weight * st.reps, 0);
          const hasPR = h.sets.some(s => s.pr);
          return (
            <div key={i} className="fade-in" style={{ ...s.card, padding: 14, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)" }}>{fmt(h.date)}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {hasPR && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "var(--amber-dim)", color: "var(--amber)" }}>PR</span>}
                  {vol > 0 && <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>{vol.toLocaleString()} lbs</span>}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {h.sets.map((st, j) => (
                  <div key={j} style={{
                    padding: "4px 8px", borderRadius: 7, fontSize: 12, fontWeight: 700,
                    background: st.warmup ? "rgba(255,255,255,0.03)" : st.pr ? "rgba(251,191,36,0.08)" : "var(--bg-3)",
                    color: st.warmup ? "var(--text-3)" : st.pr ? "var(--amber)" : "var(--text-1)",
                    border: st.pr ? "1px solid rgba(251,191,36,0.2)" : "1px solid rgba(255,255,255,0.04)",
                  }}>
                    {st.warmup ? "W " : ""}{st.weight}×{st.reps}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Main list view
  return (
    <div className="fade-in" style={{ padding: 20, paddingBottom: 100 }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 16 }}>History</h2>
      {getTrainingDayIndices(data.dayStructure).map(dayIdx => {
        const d = getDayMeta(dayIdx, data.dayStructure);
        const exList = getDayExercises(dayIdx, customProgram, data.dayStructure);
        return (
          <div key={dayIdx} style={{ ...s.card, marginBottom: 12, overflow: "hidden" }}>
            {/* Day header */}
            <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", background: `${d.color}0d` }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: d.color, textTransform: "uppercase", letterSpacing: 0.8 }}>{d.name}</span>
            </div>
            {/* Exercise rows */}
            {exList.map((ex, i) => {
              const hist = getExerciseHistory(sessions, ex.id, 2);
              const last = hist[0]?.sets?.filter(s => !s.warmup) || [];
              const topSet = last.length ? last.reduce((best, st) => st.weight > best.weight ? st : best, last[0]) : null;
              const sessionCount = getExerciseHistory(sessions, ex.id, 99).length;
              const trend = getTrend(ex.id);
              return (
                <button key={ex.id} onClick={() => setSel(ex)} style={{
                  width: "100%", textAlign: "left", display: "flex", alignItems: "center",
                  padding: "11px 14px", cursor: "pointer", fontFamily: "inherit",
                  background: "none", border: "none",
                  borderBottom: i < exList.length - 1 ? "1px solid rgba(255,255,255,0.03)" : "none",
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>{ex.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                      {sessionCount > 0 ? `${sessionCount} session${sessionCount !== 1 ? "s" : ""}` : "No sessions yet"}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {topSet && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-1)" }}>{topSet.weight} <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>lbs</span></div>
                        <div style={{ fontSize: 11, color: "var(--text-3)" }}>× {topSet.reps}</div>
                      </div>
                    )}
                    {trend && (
                      <div style={{
                        width: 24, height: 24, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                        background: trend === "up" ? "var(--green-dim)" : trend === "down" ? "var(--red-dim)" : "var(--bg-3)",
                        color: trend === "up" ? "var(--green)" : trend === "down" ? "var(--red)" : "var(--text-3)",
                      }}>
                        {trend === "up" ? "↑" : trend === "down" ? "↓" : "→"}
                      </div>
                    )}
                    {!topSet && <span style={{ fontSize: 12, color: "var(--text-3)" }}>—</span>}
                  </div>
                </button>
              );
            })}
          </div>
        );
      })}

      {Object.keys(archivedMap).length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ ...s.label, marginBottom: 8 }}>ARCHIVED</div>
          <div style={{ ...s.card }}>
            {Object.values(archivedMap).map(({ id: exId, name: exName }, i, arr) => {
              const hist = getExerciseHistory(sessions, exId, 1);
              const last = hist[0]?.sets?.filter(s => !s.warmup) || [];
              const topSet = last.length ? last.reduce((best, st) => st.weight > best.weight ? st : best, last[0]) : null;
              return (
                <button key={exId} onClick={() => setSel({ id: exId, name: exName })} style={{
                  width: "100%", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "11px 14px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                  borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.03)" : "none",
                  opacity: 0.6,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)" }}>{exName}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-2)" }}>
                    {topSet ? `${topSet.weight} × ${topSet.reps}` : "—"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
